import { useMemo } from 'react';
import { useLoaderData, useSearchParams } from 'react-router';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { Spinner } from '@rocco/components/spinner';
import { cn } from '@rocco/utils/cn';
import type { loader as projectsLoader } from '@modules/professional/page/loaders/projects-page-loader.server';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';
import { SearchFilter } from '@modules/professional/component/shared/SearchFilter';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { ProfessionalAppliedFilters } from './ProfessionalAppliedFilters';
import { PROFESSIONAL_TAB_PROJECT_FILTERS_CONFIG } from '@modules/professional/config/professional-tab-filter-configs';
import { ProfessionalTabFilterSidebar } from './ProfessionalTabFilterSidebar';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import { resetFilterStates } from '~/modules/search/util/reset-filter-states';
import { useMatchesHandleData } from '~/modules/root/hook';
import type { SearchIndex } from '~/modules/search/type';
import { IN_PAGE_SEARCH_IGNORE_FILTER_KEYS } from '~/modules/professional/config/constants';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useProfessionalTabViewMore } from '@modules/professional/hook/use-professional-tab-view-more';
import { useProfessionalHybridLoad } from '@modules/professional/hook/use-professional-hybrid-load';
import { useItemAdminData } from '~/modules/directory/hook/use-item-admin-data';
import {
    DetailTileCard,
    detailCta,
    type DetailTile,
} from '~/ui-rocco/pages/detail-shared';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { ProfessionalHeadingCtas } from './ProfessionalHeadingCtas';
import {
    DIRECTORY_DESKTOP_COL_CLASSES,
    useDirectoryDisplayColumns,
} from '~/ui-rocco/pages/directory-browse/directory-display-columns';
import { DirectoryDisplayDropdown } from '~/ui-rocco/pages/directory-browse/DirectoryDisplayDropdown';

const PROJECTS_DESCRIPTION =
    "Explore the projects we've brought to life. Browse our completed work to see how we approach design, craft and detail across every build.";

/**
 * Revamped body for the Projects tab (/case-studies) on a dark editorial
 * section per the Figma: an "(All Projects)" divider, the collapsible left
 * filters reused from the legacy tab (starting collapsed behind a toggle,
 * dark-themed *scoped to this page* via the `[&_*]` overrides), and a 3-column
 * dark editorial grid with infinite scroll. The name / stats / CTA sit in the
 * persistent profile header above, so the tab goes straight to results.
 */
export const ProfessionalProjectsGridSection = () => {
    const {
        projectTilesRocco,
        pagination,
        filterStates,
        filterStatesRocco,
        defaultViewProjectsBy,
        aggregateScopeFilters,
    } = useLoaderData<typeof projectsLoader>();
    const { ShowOwnProjectsOnly, URLSegment, Title } =
        useProfessionalInitialRouteData();
    const { desktop } = useAppDisplayModeContext();
    // The Projects tab is the dark editorial theme on desktop, but reads better
    // white on mobile (matching the other tabs); flip every themed bit off this.
    const isDark = desktop;

    // Persisted in the site-wide `showFilter` cookie so the show/hide choice
    // survives navigation and stays consistent with the browse pages.
    const { filterDrawerOpen, toggleFilterDrawer } =
        useFilterDrawerPreference();

    // Keyed separately from the `projects` browse grid so a choice made here
    // does not follow the reader out to /projects. Default matches that page.
    const { columns, setColumns } = useDirectoryDisplayColumns(
        'professional-projects',
        3,
    );

    const {
        tiles: loadedTiles,
        hasMore,
        isLoading,
        loadMore,
    } = useProfessionalTabViewMore({
        initialTiles:
            projectTilesRocco as TileViewModel<ProjectTileViewModel>[],
        pagination,
        urlSegment: URLSegment,
        searchType: 'project',
    });

    const [searchParams] = useSearchParams();
    const { showViewMore, onViewMore, infiniteEnabled, sentinelRef } =
        useProfessionalHybridLoad({
            hasMore,
            isLoading,
            loadMore,
            resetKey: searchParams.toString(),
        });

    const adminDataMap = useItemAdminData(
        loadedTiles.map((tile) => tile.model.id),
    );
    const professionalLink = `/professional/${URLSegment}`;

    const tiles: DetailTile[] = loadedTiles.map((tile) => ({
        id: tile.model.id,
        link: tile.model.link,
        title: tile.model.title,
        images: tile.model.images,
        subtitle: tile.model.professional?.name,
        professional: {
            id: tile.model.professional?.id,
            name: tile.model.professional?.name,
        },
        pinType: 'ProjectPin',
        adminData: adminDataMap[tile.model.id],
        // Associated / collaborated projects can belong to another
        // professional — enquiry and admin links must follow the tile's own
        // professional, not the page's.
        professionalLink: tile.model.professional?.link || professionalLink,
    }));

    const ignoreFilterKeys = useMemo(() => {
        const keys = [...IN_PAGE_SEARCH_IGNORE_FILTER_KEYS];
        if (ShowOwnProjectsOnly) keys.push('viewProjectsBy');
        return keys;
    }, [ShowOwnProjectsOnly]);

    const category = useMatchesHandleData<SearchIndex>('category', 'project');
    const searchFilters = useSearchFilters({
        category,
        initialStates: filterStates,
        isCategoryPage: false,
        ignoreFilterKeys,
    });

    return (
        <section
            className={cn(
                'w-full overflow-x-clip',
                isDark ? 'bg-[#181818] text-white' : 'bg-white text-[#181818]',
            )}
        >
            {/* Desktop keeps the editorial title + intro + CTA cluster; on mobile
                the persistent social-profile header carries the identity/CTAs and
                the tab goes straight to results. */}
            {desktop && (
                <>
                    <div className="mx-auto w-full max-w-[120rem] md:px-[3.132rem] md:pt-[3rem]">
                        <div className="flex md:items-start md:justify-between">
                            <h2 className="m-0 font-sans font-normal text-white md:text-[12.5rem] md:leading-[0.92] md:tracking-[-0.12rem]">
                                Projects
                            </h2>
                            <ProfessionalHeadingCtas theme="dark" />
                        </div>
                        <p className="m-0 font-sans leading-[1.25] font-normal text-white md:mt-[5.9375rem] md:max-w-[60.375rem] md:text-[2.0625rem]">
                            {PROJECTS_DESCRIPTION}
                        </p>
                    </div>
                    <div className="mx-auto w-full max-w-[120rem] md:px-[3.132rem] md:pt-[10rem]">
                        <div className="h-px w-full bg-white/20" />
                        <SectionLabel className="mt-[1.062rem] block text-white md:text-[2rem]">
                            (All Projects)
                        </SectionLabel>
                    </div>
                </>
            )}
            {/* Filters + grid, straight to results. `[&_*]:text-white!` scopes
                the dark theme to this page only — legacy filter usages stay
                light. */}
            <div className="mx-auto w-full max-w-[120rem] max-md:px-6 max-md:pt-5 max-md:pb-14 md:px-[3.132rem] md:pt-[2.4rem] md:pb-[7.5rem]">
                <CollapsiblePageLayout
                    paddingClassName="px-0"
                    sidePanel={
                        desktop &&
                        filterStatesRocco && (
                            // The sidebar self-themes via the directory browse
                            // theme context — no CSS overrides needed here.
                            <div className="h-full md:pt-[1.8rem]">
                                <ProfessionalTabFilterSidebar
                                    searchIndex="project"
                                    config={
                                        PROFESSIONAL_TAB_PROJECT_FILTERS_CONFIG
                                    }
                                    initialFilterStates={filterStatesRocco}
                                    theme={isDark ? 'dark' : 'light'}
                                    extraIgnoreFilterKeys={
                                        ShowOwnProjectsOnly
                                            ? ['viewProjectsBy']
                                            : undefined
                                    }
                                    defaultViewProjectsBy={
                                        defaultViewProjectsBy ?? undefined
                                    }
                                    aggregateScopeFilters={
                                        aggregateScopeFilters
                                    }
                                />
                            </div>
                        )
                    }
                    hideSidePanel={!filterDrawerOpen}
                >
                    <SearchFilter
                        searchFilters={searchFilters}
                        filterDrawerOpen={filterDrawerOpen}
                        toggleFilterDrawer={toggleFilterDrawer}
                        searchBarVariant="revamp"
                        searchBarTheme={isDark ? 'dark' : 'light'}
                        searchPlaceholder={`Search Project by ${Title}`}
                        displayControl={
                            desktop ? (
                                <DirectoryDisplayDropdown
                                    columns={columns}
                                    onColumnsChange={setColumns}
                                />
                            ) : null
                        }
                    />
                    {/* The applied-filter bar is desktop-only; on mobile the
                        Refine button's badge + sheet already surface these. */}
                    {desktop &&
                        searchFilters.appliedFilters &&
                        searchFilters.appliedFilters.length > 0 && (
                            <ProfessionalAppliedFilters
                                filterItems={searchFilters.appliedFilters}
                                theme={isDark ? 'dark' : 'light'}
                                onClearAll={() =>
                                    searchFilters.resetFilters(
                                        resetFilterStates(
                                            searchFilters.filterStates,
                                            [...ignoreFilterKeys, 'category'],
                                        ),
                                    )
                                }
                            />
                        )}

                    {tiles.length === 0 ? (
                        <div className={isDark ? '[&_*]:text-white!' : ''}>
                            <NoSearchResults resultsType={'projects'} />
                        </div>
                    ) : (
                        <>
                            <div
                                className={cn(
                                    'grid grid-cols-1 gap-x-[1.375rem] gap-y-8 pt-[2.4rem] md:gap-y-[6.25rem]',
                                    DIRECTORY_DESKTOP_COL_CLASSES[columns],
                                )}
                            >
                                {tiles.map((tile) => (
                                    <DetailTileCard
                                        key={tile.id}
                                        tile={tile}
                                        theme={isDark ? 'dark' : 'light'}
                                        mediaClassName={cn(
                                            'max-md:aspect-[592/430] md:aspect-[592/430]',
                                            isDark
                                                ? 'bg-neutral-800'
                                                : 'bg-[#f9f6f6]',
                                        )}
                                    />
                                ))}
                            </div>
                            {infiniteEnabled && hasMore && (
                                <div
                                    ref={sentinelRef}
                                    aria-hidden="true"
                                    style={{ height: 1 }}
                                />
                            )}
                            {showViewMore && (
                                <div className="flex justify-center pt-[3rem]">
                                    <button
                                        type="button"
                                        onClick={onViewMore}
                                        className={detailCta(
                                            isDark
                                                ? 'outlined-dark'
                                                : 'outlined-light',
                                            'max-md:h-[3.125rem] max-md:text-sm',
                                        )}
                                    >
                                        View more
                                    </button>
                                </div>
                            )}
                            {isLoading && (
                                <div className="flex justify-center pt-[3rem]">
                                    <Spinner
                                        className={cn(
                                            'size-8',
                                            isDark
                                                ? 'text-white'
                                                : 'text-black',
                                        )}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </CollapsiblePageLayout>
            </div>
        </section>
    );
};
